/**
 * Dice - Core dice entity
 *
 * Handles:
 * - 3D mesh + physics body sync
 * - Face detection (reading dice value)
 * - Visual states (held, rolling, stopped)
 *
 * Supports both procedural dice (BoxGeometry fallback) and custom GLB models
 * via DiceAsset from the DiceAssetManager.
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { PhysicsWorld, CollisionGroups } from '../../engine/physics/PhysicsWorld';
import type { DiceAsset } from './assets/DiceAssetManager';
import { sound } from '../../engine/audio/sound';
import { SoundId, faceCollisionSound } from '../../engine/audio/SoundCatalog';

// ── Collision-sound tuning ──────────────────────────────────────────────────
// Below MIN_IMPACT the contact is resting jitter / a gentle settle — no sound.
// Impact above MIN_IMPACT scales volume up to full at MIN_IMPACT+IMPACT_RANGE.
// COOLDOWN throttles a single die so one bounce (many contact points per step)
// fires at most one sound.
const COLLIDE_MIN_IMPACT = 1.5;   // m/s along the contact normal
const COLLIDE_IMPACT_RANGE = 8;   // m/s above min → full volume (per-face path)
const COLLIDE_MIN_VOLUME = 0.15;
const COLLIDE_COOLDOWN_MS = 45;
// Clatter (dice-vs-dice + wall-as-dice) loudness curve. The per-face range
// above saturates at ~9.5 m/s — at throw power 35 nearly every mid-flight
// contact maxed out, so the clatter played at one flat volume. Map a much
// wider impact window onto volume, and tie playback rate to impact so hard
// hits read brighter as well as louder.
const CLATTER_IMPACT_FULL = 20;   // m/s at which clatter reaches full volume
const CLATTER_MIN_VOLUME = 0.12;
const CLATTER_RATE_SPREAD = 0.1;  // ± rate swing across the impact range
const CLATTER_RATE_JITTER = 0.06; // random rate component (replaces catalog vary)
// Floor contacts fire constantly while dice roll/settle, which reads as a
// clicky machine-gun. Wall hits are sparser and punchier, so play those only
// for now. Flip to true to bring back per-face floor impacts.
const COLLIDE_FLOOR_ENABLED = false;
// Experiment: route wall hits through the dice-vs-dice clatter sample instead
// of the per-face DICE_FACE_* sounds. Set false to use the per-face wall set.
const COLLIDE_WALL_AS_DICE = true;

/** Minimal shape of the cannon-es 'collide' event we read. */
interface CollideEvent {
  body: CANNON.Body;
  contact: CANNON.ContactEquation;
}

// Default face normals for standard dice (opposite faces sum to 7)
// Order: [+Z, -Z, +X, -X, +Y, -Y] → pip values [1, 6, 2, 5, 3, 4]
const DEFAULT_FACE_NORMALS: Record<number, THREE.Vector3> = {
  1: new THREE.Vector3(0, 0, 1),   // Front face
  6: new THREE.Vector3(0, 0, -1),  // Back face
  2: new THREE.Vector3(1, 0, 0),   // Right face
  5: new THREE.Vector3(-1, 0, 0),  // Left face
  3: new THREE.Vector3(0, 1, 0),   // Top face
  4: new THREE.Vector3(0, -1, 0),  // Bottom face
};

// Normal directions in the order that DiceModel.faces[] uses
// faces[0]=front(+Z), faces[1]=back(-Z), faces[2]=right(+X), faces[3]=left(-X), faces[4]=top(+Y), faces[5]=bottom(-Y)
const NORMAL_DIRECTIONS: THREE.Vector3[] = [
  new THREE.Vector3(0, 0, 1),   // [0] front +Z
  new THREE.Vector3(0, 0, -1),  // [1] back -Z
  new THREE.Vector3(1, 0, 0),   // [2] right +X
  new THREE.Vector3(-1, 0, 0),  // [3] left -X
  new THREE.Vector3(0, 1, 0),   // [4] top +Y
  new THREE.Vector3(0, -1, 0),  // [5] bottom -Y
];

export interface DiceConfig {
  size?: number;
  color?: number;
  position?: { x: number; y: number; z: number };
}

// Debug: set true to show green wireframe physics bounding box
const DEBUG_PHYSICS_BOX = false;

export class Dice {
  readonly mesh: THREE.Object3D;
  readonly body: CANNON.Body;

  /** The DiceAsset if this dice uses a custom model (for pool release) */
  readonly asset: DiceAsset | null;
  /** Physics half-extents [x,y,z] — may differ per axis for custom models */
  readonly physicsHalfExtents: [number, number, number];

  private physics: PhysicsWorld;
  private size: number;
  private value: number = 1;
  private isHeld = false;

  // Face normals — built from DiceAsset.faceMap or default
  private faceNormals: Record<number, THREE.Vector3>;

  // Debug wireframe for physics box
  private debugBox: THREE.LineSegments | null = null;

  // For face detection
  private upVector = new THREE.Vector3(0, 1, 0);
  private tempVector = new THREE.Vector3();
  private tempQuaternion = new THREE.Quaternion();

  // Collision-sound state (per-die throttle + reusable contact-direction vec)
  private lastCollideAudioMs = 0;
  // Suppress dice-vs-dice clatter until this die has contacted a non-dice
  // surface (floor/wall) in the current flight. Freshly released dice are
  // clustered from the hand, so they clack against each other instantly —
  // a phantom "hit" before anything visibly happens. Reset in release()
  // (the single entry point into free flight: local throw, drop-without-
  // throw, server-echo throw, opponent throw all pass through it).
  private touchedSurfaceThisFlight = false;
  private collideDir = new THREE.Vector3();
  private readonly onCollide = (e: CollideEvent): void => this.handleCollisionSound(e);

  constructor(physics: PhysicsWorld, scene: THREE.Scene, config: DiceConfig = {}, asset?: DiceAsset) {
    this.physics = physics;
    this.size = config.size ?? 1;
    this.asset = asset ?? null;

    // Build mesh — custom GLB or procedural fallback
    let physicsHE: [number, number, number];

    if (asset) {
      this.mesh = asset.mesh;

      // Scale GLB to match game dice size
      const targetHalf = this.size / 2;
      const maxHalf = Math.max(asset.halfExtents[0], asset.halfExtents[1], asset.halfExtents[2]);
      const scaleFactor = targetHalf / maxHalf;
      asset.mesh.scale.setScalar(scaleFactor);

      // Scaled half-extents for physics
      physicsHE = [
        asset.halfExtents[0] * scaleFactor,
        asset.halfExtents[1] * scaleFactor,
        asset.halfExtents[2] * scaleFactor,
      ];

      scene.add(this.mesh);
      this.faceNormals = this.buildFaceNormals(asset.faceMap);
    } else {
      this.mesh = this.createMesh(config.color ?? 0xcc0000);
      scene.add(this.mesh);
      this.faceNormals = DEFAULT_FACE_NORMALS;
      const halfSize = this.size / 2;
      physicsHE = [halfSize, halfSize, halfSize];
    }

    this.physicsHalfExtents = physicsHE;

    // Debug: show physics bounding box as green wireframe
    if (DEBUG_PHYSICS_BOX) {
      const sx = this.mesh.scale.x || 1;
      const sy = this.mesh.scale.y || 1;
      const sz = this.mesh.scale.z || 1;
      const debugGeo = new THREE.BoxGeometry(
        (physicsHE[0] * 2) / sx,
        (physicsHE[1] * 2) / sy,
        (physicsHE[2] * 2) / sz,
      );
      const edges = new THREE.EdgesGeometry(debugGeo);
      const debugMat = new THREE.LineBasicMaterial({ color: 0x00ff00, depthTest: false });
      this.debugBox = new THREE.LineSegments(edges, debugMat);
      this.debugBox.renderOrder = 999;
      this.mesh.add(this.debugBox);
      debugGeo.dispose();
    }

    // Reuse cached physics body from asset pool (PhoneDice pattern) or create new
    const pos = config.position ?? { x: 0, y: 2, z: 0 };
    if (asset?.body) {
      this.body = asset.body;
      physics.reuseBody(this.body, pos);
    } else {
      const shape = new CANNON.Box(new CANNON.Vec3(physicsHE[0], physicsHE[1], physicsHE[2]));
      this.body = physics.createBody({
        mass: 1,
        shape,
        material: physics.diceMaterial,
        position: pos,
        linearDamping: 0.2,
        angularDamping: 0.4,
        collisionFilterGroup: CollisionGroups.DICE,
        collisionFilterMask: CollisionGroups.TABLE | CollisionGroups.WALL | CollisionGroups.DICE | CollisionGroups.BILL,
      });
      // Store body on asset for future pool reuse
      if (asset) asset.body = this.body;
    }

    // Heal inertia for pool-reused bodies whose tensor was corrupted by a
    // previous pickup/release cycle at non-identity rotation. Safe no-op
    // for freshly created bodies (already at identity).
    this.updateMassPropertiesSafe();

    // Collision sounds. Balanced add/remove with dispose() keeps a single
    // listener on a pooled body across reuse (releaseDiceAssets disposes the
    // old Dice before a new one re-wraps the same asset.body).
    this.body.addEventListener('collide', this.onCollide as (e: unknown) => void);

    if (config.position) {
      this.setPosition(config.position);
    }
  }

  /**
   * Play a collision sound on dice→floor / dice→wall / dice→dice impacts.
   * Floor and wall get a per-face sound (which face of the die struck the
   * surface); dice-vs-dice reuses the shared clatter sample. Volume scales with
   * impact; a per-die cooldown collapses the many contact points of one bounce
   * into a single hit. Read-only — never touches the body, so simulation and
   * client/server determinism are unaffected.
   */
  private handleCollisionSound(e: CollideEvent): void {
    if (this.isHeld) return;

    const surface = e.body.material?.name; // 'dice' | 'table' | 'wall' | 'bill'
    if (surface !== 'dice' && surface !== 'table' && surface !== 'wall') return;

    // First non-dice contact of this flight unlocks dice-vs-dice clatter.
    // Recorded BEFORE the impact/cooldown gates — a soft floor graze still
    // counts as "this die has arrived at the table".
    if (surface === 'table' || surface === 'wall') {
      this.touchedSurfaceThisFlight = true;
    } else if (!this.touchedSurfaceThisFlight) {
      // Dice-vs-dice while still in-hand flight (clustered from release) —
      // phantom clack, stay silent.
      return;
    }

    // Floor disabled for now — bail before touching the cooldown so a floor
    // contact can't suppress a near-simultaneous wall hit.
    if (surface === 'table' && !COLLIDE_FLOOR_ENABLED) return;

    const impact = Math.abs(e.contact.getImpactVelocityAlongNormal());
    if (impact < COLLIDE_MIN_IMPACT) return;

    const now = performance.now();
    if (now - this.lastCollideAudioMs < COLLIDE_COOLDOWN_MS) return;
    this.lastCollideAudioMs = now;

    if (surface === 'dice' || (surface === 'wall' && COLLIDE_WALL_AS_DICE)) {
      const impact01 = Math.min(
        1,
        (impact - COLLIDE_MIN_IMPACT) / (CLATTER_IMPACT_FULL - COLLIDE_MIN_IMPACT),
      );
      const clatterVolume = CLATTER_MIN_VOLUME + (1 - CLATTER_MIN_VOLUME) * impact01;
      // Centered rate: soft kisses slightly deeper, violent hits slightly
      // brighter, plus the random jitter the catalog vary used to add
      // (explicit rate overrides catalog jitter, so fold it in here).
      const rate = 1
        + (impact01 - 0.5) * CLATTER_RATE_SPREAD
        + (Math.random() - 0.5) * CLATTER_RATE_JITTER;
      sound.play(SoundId.DICE_DICE, { volume: clatterVolume, rate });
      return;
    }

    const volume = Math.min(
      1,
      COLLIDE_MIN_VOLUME + (impact - COLLIDE_MIN_IMPACT) / COLLIDE_IMPACT_RANGE,
    );

    // Floor / wall: the contacting face is the one whose world normal points
    // toward the contact point. ri/rj is the world-frame vector from each
    // body's centre to the contact; pick the die's side.
    const r = e.contact.bi === this.body ? e.contact.ri : e.contact.rj;
    this.collideDir.set(r.x, r.y, r.z);
    if (this.collideDir.lengthSq() < 1e-6) return;
    this.collideDir.normalize();

    const face = this.faceTowards(this.collideDir);
    sound.play(faceCollisionSound(surface === 'wall' ? 'wall' : 'floor', face), { volume });
  }

  /**
   * Cannon-ES `Body.updateMassProperties()` approximates inertia from the
   * body's current AABB, which is rotation-dependent: a rotated cube has a
   * larger AABB than the shape itself, so invInertia computed on a rotated
   * body is smaller than it should be. The server creates each die at
   * identity and never re-runs updateMassProperties, so it gets the correct
   * tensor. The client calls updateMassProperties during pickup/release/
   * throw/reset, often while the body is rotated mid-pickup — which makes
   * client simulation rotationally under-responsive vs the server and causes
   * HI_LO reconciliation snapshots to visibly snap dice.
   *
   * Computing at identity quat inside a save/restore wrapper matches the
   * server's behaviour without touching Cannon.
   */
  private updateMassPropertiesSafe(): void {
    const qx = this.body.quaternion.x;
    const qy = this.body.quaternion.y;
    const qz = this.body.quaternion.z;
    const qw = this.body.quaternion.w;
    this.body.quaternion.set(0, 0, 0, 1);
    this.body.updateMassProperties();
    this.body.quaternion.set(qx, qy, qz, qw);
    this.body.updateInertiaWorld(true);
  }

  /**
   * Build face normals from DiceModel.faces array.
   * faces[i] = pip value for normal direction NORMAL_DIRECTIONS[i]
   */
  private buildFaceNormals(faceMap: number[]): Record<number, THREE.Vector3> {
    if (!faceMap || faceMap.length !== 6) return DEFAULT_FACE_NORMALS;

    const normals: Record<number, THREE.Vector3> = {};
    for (let i = 0; i < 6; i++) {
      normals[faceMap[i]] = NORMAL_DIRECTIONS[i];
    }
    return normals;
  }

  private createMesh(color: number): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(this.size, this.size, this.size);

    const material = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.3,
      metalness: 0.1,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    // Cast onto the table but don't self-receive — avoids pip shadow-acne on
    // low-precision (Android WebView) shadow sampling. Matches the GLB dice.
    mesh.receiveShadow = false;

    // Add pips to all faces
    this.addPipsToMesh(mesh);

    return mesh;
  }

  /**
   * Add pip dots to all faces of the dice
   * Standard dice: opposite faces sum to 7
   */
  private addPipsToMesh(mesh: THREE.Mesh): void {
    const pipGeometry = new THREE.CircleGeometry(this.size * 0.08, 16);
    const pipMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });

    // Face definitions: direction vector and pip positions
    // Pips are positioned relative to face center, scaled by size
    const faces: Array<{
      dir: [number, number, number];
      pips: Array<[number, number]>;
    }> = [
      // 1 pip - front face (z+)
      { dir: [0, 0, 1], pips: [[0, 0]] },
      // 2 pips - back face (z-)
      { dir: [0, 0, -1], pips: [[-0.25, 0.25], [0.25, -0.25]] },
      // 3 pips - right face (x+)
      { dir: [1, 0, 0], pips: [[-0.25, 0.25], [0, 0], [0.25, -0.25]] },
      // 4 pips - left face (x-)
      { dir: [-1, 0, 0], pips: [[-0.25, 0.25], [0.25, 0.25], [-0.25, -0.25], [0.25, -0.25]] },
      // 5 pips - top face (y+)
      { dir: [0, 1, 0], pips: [[-0.25, 0.25], [0.25, 0.25], [0, 0], [-0.25, -0.25], [0.25, -0.25]] },
      // 6 pips - bottom face (y-)
      { dir: [0, -1, 0], pips: [[-0.25, 0.25], [0.25, 0.25], [-0.25, 0], [0.25, 0], [-0.25, -0.25], [0.25, -0.25]] },
    ];

    const halfSize = this.size / 2;
    const offset = halfSize + 0.01; // Slightly above surface

    faces.forEach((face) => {
      face.pips.forEach((pos) => {
        const pip = new THREE.Mesh(pipGeometry, pipMaterial);

        // Position pip based on face direction
        if (face.dir[0] !== 0) {
          // X faces (left/right)
          pip.position.set(
            face.dir[0] * offset,
            pos[1] * this.size,
            pos[0] * this.size
          );
          pip.rotation.y = (face.dir[0] * Math.PI) / 2;
        } else if (face.dir[1] !== 0) {
          // Y faces (top/bottom)
          pip.position.set(
            pos[0] * this.size,
            face.dir[1] * offset,
            pos[1] * this.size
          );
          pip.rotation.x = (-face.dir[1] * Math.PI) / 2;
        } else {
          // Z faces (front/back)
          pip.position.set(
            pos[0] * this.size,
            pos[1] * this.size,
            face.dir[2] * offset
          );
          if (face.dir[2] < 0) pip.rotation.y = Math.PI;
        }

        mesh.add(pip);
      });
    });
  }

  /**
   * Visual-only correction offset, decayed each sync.
   * When reconciliation snaps the physics body, we capture the previous mesh
   * pose as a delta here and fade the delta to zero over `correctionDuration`
   * seconds — so the mesh smoothly slides toward the new body pose instead of
   * teleporting. Physics is NOT affected.
   */
  private correctionFromPos: THREE.Vector3 | null = null;
  private correctionFromQuat: THREE.Quaternion | null = null;
  private correctionElapsed = 0;
  private correctionDuration = 0;
  private _tmpTargetPos = new THREE.Vector3();
  private _tmpTargetQuat = new THREE.Quaternion();

  /**
   * Start a visual-only correction to smoothly lerp the mesh from its current
   * pose toward whatever the body pose is (or becomes) over `durationMs`.
   * Call this BEFORE snapping the body to a new reconciled state.
   */
  startVisualCorrection(durationMs: number = 150): void {
    // Capture current mesh pose (the "from" state the lerp animates out of).
    if (!this.correctionFromPos) this.correctionFromPos = new THREE.Vector3();
    if (!this.correctionFromQuat) this.correctionFromQuat = new THREE.Quaternion();
    this.correctionFromPos.copy(this.mesh.position);
    this.correctionFromQuat.copy(this.mesh.quaternion);
    this.correctionElapsed = 0;
    this.correctionDuration = Math.max(0.001, durationMs / 1000);
  }

  /**
   * Sync mesh to physics body (call in render loop)
   * Uses interpolated positions for smooth visuals between physics steps.
   * If a visual correction is active, lerps from the captured pre-snap mesh
   * pose toward the current body pose — hides reconciliation teleports.
   */
  syncToPhysics(deltaTime: number = 0): void {
    // Target = interpolated body pose (same as before).
    const interpPos = this.physics.getInterpolatedPosition(this.body);
    const interpQuat = this.physics.getInterpolatedQuaternion(this.body);
    if (interpPos && interpQuat) {
      this._tmpTargetPos.set(interpPos.x, interpPos.y, interpPos.z);
      this._tmpTargetQuat.set(interpQuat.x, interpQuat.y, interpQuat.z, interpQuat.w);
    } else {
      this._tmpTargetPos.copy(this.body.position as unknown as THREE.Vector3);
      this._tmpTargetQuat.copy(this.body.quaternion as unknown as THREE.Quaternion);
    }

    // If a correction is active, render somewhere between the pre-snap pose
    // and the target pose; ease-out so the correction tapers at the end.
    if (this.correctionFromPos && this.correctionFromQuat && this.correctionDuration > 0) {
      this.correctionElapsed += deltaTime;
      const t = Math.min(1, this.correctionElapsed / this.correctionDuration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      this.mesh.position.lerpVectors(this.correctionFromPos, this._tmpTargetPos, eased);
      this.mesh.quaternion.slerpQuaternions(this.correctionFromQuat, this._tmpTargetQuat, eased);
      if (t >= 1) {
        this.correctionFromPos = null;
        this.correctionFromQuat = null;
        this.correctionDuration = 0;
      }
      return;
    }

    // No correction in flight — render at the body pose directly.
    this.mesh.position.copy(this._tmpTargetPos);
    this.mesh.quaternion.copy(this._tmpTargetQuat);
  }

  /**
   * Read the top face value (the face whose world normal points most upward).
   */
  readValue(): number {
    this.value = this.faceTowards(this.upVector);
    return this.value;
  }

  /**
   * Return the die value (1-6) of the face whose world normal is most aligned
   * with `dir` (world space). readValue() is just `faceTowards(+Y)`; collision
   * code passes the direction toward the contacting surface.
   */
  faceTowards(dir: THREE.Vector3): number {
    this.tempQuaternion.set(
      this.body.quaternion.x,
      this.body.quaternion.y,
      this.body.quaternion.z,
      this.body.quaternion.w
    );

    let maxDot = -Infinity;
    let bestFace = 1;
    for (const [face, normal] of Object.entries(this.faceNormals)) {
      this.tempVector.copy(normal);
      this.tempVector.applyQuaternion(this.tempQuaternion);
      const dot = this.tempVector.dot(dir);
      if (dot > maxDot) {
        maxDot = dot;
        bestFace = parseInt(face);
      }
    }
    return bestFace;
  }

  /**
   * Get current value (cached from last readValue)
   */
  getValue(): number {
    return this.value;
  }

  /**
   * Force the dice to show a specific value.
   * @param value - Face value (1-6) to show on top
   * @param yaw - Optional deterministic yaw rotation (radians). Defaults to 0 for determinism.
   */
  setValue(value: number, yaw = 0): void {
    const faceNormal = this.faceNormals[value];
    if (!faceNormal) return;

    const up = new THREE.Vector3(0, 1, 0);
    const target = faceNormal.clone().normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(target, up);
    const yawQuat = new THREE.Quaternion().setFromAxisAngle(up, yaw);
    quat.multiply(yawQuat);

    this.physics.setQuaternion(this.body, {
      x: quat.x,
      y: quat.y,
      z: quat.z,
      w: quat.w,
    });
    this.mesh.quaternion.copy(quat);
    this.value = value;
  }

  /**
   * Check if dice is at rest
   */
  isStopped(): boolean {
    return this.physics.isSleeping(this.body);
  }

  /**
   * Check if dice is resting on a valid face (not an edge or corner)
   * Returns true if one of the 6 face normals is pointing mostly upward
   */
  isOnValidFace(): boolean {
    this.tempQuaternion.set(
      this.body.quaternion.x,
      this.body.quaternion.y,
      this.body.quaternion.z,
      this.body.quaternion.w
    );

    for (const normal of Object.values(this.faceNormals)) {
      this.tempVector.copy(normal);
      this.tempVector.applyQuaternion(this.tempQuaternion);
      const dot = this.tempVector.dot(this.upVector);
      if (dot > 0.95) {
        return true; // A face is pointing up
      }
    }
    return false; // On edge or corner
  }

  /**
   * Deterministic nudge toward nearest valid face
   * Uses current orientation to calculate nudge direction (no randomness)
   */
  nudgeToNearestFace(): void {
    this.tempQuaternion.set(
      this.body.quaternion.x,
      this.body.quaternion.y,
      this.body.quaternion.z,
      this.body.quaternion.w
    );

    // Find which face normal is closest to pointing up
    let bestDot = -Infinity;
    let bestNormal: THREE.Vector3 | null = null;

    for (const normal of Object.values(this.faceNormals)) {
      this.tempVector.copy(normal);
      this.tempVector.applyQuaternion(this.tempQuaternion);
      const dot = this.tempVector.dot(this.upVector);
      if (dot > bestDot) {
        bestDot = dot;
        bestNormal = normal;
      }
    }

    if (bestNormal) {
      // Calculate torque to rotate the best face toward up
      // Cross product of current "best face direction" with up gives rotation axis
      this.tempVector.copy(bestNormal);
      this.tempVector.applyQuaternion(this.tempQuaternion);

      const torqueAxis = new THREE.Vector3();
      torqueAxis.crossVectors(this.tempVector, this.upVector);

      // Scale torque by how far off we are (larger angle = stronger nudge)
      const angleOff = Math.acos(Math.min(1, Math.max(-1, bestDot)));
      const torqueStrength = angleOff * 2; // Tune this multiplier as needed

      this.body.angularVelocity.set(
        torqueAxis.x * torqueStrength,
        torqueAxis.y * torqueStrength,
        torqueAxis.z * torqueStrength
      );
      this.physics.wakeUp(this.body);
    }
  }

  /**
   * Get current speed
   */
  getSpeed(): number {
    return this.physics.getSpeed(this.body);
  }

  /**
   * Get dice position
   */
  getPosition(): { x: number; y: number; z: number } {
    return {
      x: this.body.position.x,
      y: this.body.position.y,
      z: this.body.position.z,
    };
  }

  /**
   * Get dice rotation as quaternion
   */
  getRotation(): { x: number; y: number; z: number; w: number } {
    return {
      x: this.body.quaternion.x,
      y: this.body.quaternion.y,
      z: this.body.quaternion.z,
      w: this.body.quaternion.w,
    };
  }

  /**
   * Get dice velocity
   */
  getVelocity(): { x: number; y: number; z: number } {
    return {
      x: this.body.velocity.x,
      y: this.body.velocity.y,
      z: this.body.velocity.z,
    };
  }

  /**
   * Get dice angular velocity
   */
  getAngularVelocity(): { x: number; y: number; z: number } {
    return {
      x: this.body.angularVelocity.x,
      y: this.body.angularVelocity.y,
      z: this.body.angularVelocity.z,
    };
  }

  /**
   * Check if dice is sliding (moving slowly with low rotation)
   * This happens when dice slides along a wall or another die
   */
  isSliding(): boolean {
    const vel = this.body.velocity;
    const angVel = this.body.angularVelocity;

    // Calculate linear speed (horizontal only - ignore Y for sliding detection)
    const horizontalSpeed = Math.sqrt(vel.x * vel.x + vel.z * vel.z);

    // Calculate angular speed
    const angularSpeed = Math.sqrt(
      angVel.x * angVel.x + angVel.y * angVel.y + angVel.z * angVel.z
    );

    // Sliding: moving horizontally but not rotating much
    // Thresholds tuned for dice physics
    const isMovingHorizontally = horizontalSpeed > 0.1;
    const isNotRolling = angularSpeed < 0.3;
    const isMovingSlowly = horizontalSpeed < 1.0;

    return isMovingHorizontally && isNotRolling && isMovingSlowly;
  }

  /**
   * Dampen dice velocity to help it settle when sliding
   * Only reduces energy, never adds it
   */
  dampenSliding(): void {
    // Only dampen, don't add energy - this prevents oscillation
    this.body.velocity.x *= 0.3;
    this.body.velocity.z *= 0.3;
    this.body.angularVelocity.x *= 0.5;
    this.body.angularVelocity.y *= 0.5;
    this.body.angularVelocity.z *= 0.5;
  }

  /**
   * Set dice velocity (for server-authoritative state sync)
   */
  setVelocity(vel: { x: number; y: number; z: number }): void {
    this.body.velocity.set(vel.x, vel.y, vel.z);
  }

  /**
   * Set dice angular velocity (for server-authoritative state sync)
   */
  setAngularVelocity(angVel: { x: number; y: number; z: number }): void {
    this.body.angularVelocity.set(angVel.x, angVel.y, angVel.z);
  }

  /**
   * Set dice position
   */
  setPosition(pos: { x: number; y: number; z: number }): void {
    this.physics.setPosition(this.body, pos);
    this.mesh.position.set(pos.x, pos.y, pos.z);
  }

  /**
   * Set dice rotation from Euler angles
   */
  setRotation(euler: { x: number; y: number; z: number }): void {
    const quat = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(euler.x, euler.y, euler.z)
    );
    this.physics.setQuaternion(this.body, {
      x: quat.x,
      y: quat.y,
      z: quat.z,
      w: quat.w,
    });
    this.mesh.quaternion.copy(quat);
  }

  /**
   * Set dice rotation from quaternion (for server-authoritative state sync)
   */
  setRotationFromQuaternion(quat: { x: number; y: number; z: number; w: number }): void {
    this.physics.setQuaternion(this.body, quat);
    this.mesh.quaternion.set(quat.x, quat.y, quat.z, quat.w);
  }

  /**
   * Add incremental rotation (for continuous spin while held)
   */
  addRotation(delta: { x: number; y: number; z: number }): void {
    this.mesh.rotation.x += delta.x;
    this.mesh.rotation.y += delta.y;
    this.mesh.rotation.z += delta.z;

    // Sync to physics body
    const quat = this.mesh.quaternion;
    this.physics.setQuaternion(this.body, {
      x: quat.x,
      y: quat.y,
      z: quat.z,
      w: quat.w,
    });
  }

  /**
   * Pick up the dice (disable physics)
   */
  pickup(): void {
    this.isHeld = true;
    this.body.mass = 0;
    this.updateMassPropertiesSafe();
    this.physics.resetBody(this.body);
    // Disable collisions so held dice don't interfere with rolling dice
    this.body.collisionFilterMask = CollisionGroups.NONE;
  }

  /**
   * Release the dice (enable physics)
   */
  /**
   * Re-arm the dice-vs-dice clatter mute for a fresh flight. The throw
   * pre-run steps the live world at release time: its simulated floor/wall
   * contacts run handleCollisionSound and set touchedSurfaceThisFlight even
   * though the audio is suspended. preRunThrow restores BODY state, not
   * instance flags — so the pre-run caller must call this afterwards or the
   * real flight starts with the mute already unlocked.
   */
  rearmFlightSurfaceMute(): void {
    this.touchedSurfaceThisFlight = false;
  }

  release(): void {
    this.isHeld = false;
    this.body.mass = 1;
    this.updateMassPropertiesSafe();
    // Re-enable collisions with table, wall, other dice, and props
    this.body.collisionFilterMask = CollisionGroups.TABLE | CollisionGroups.WALL | CollisionGroups.DICE | CollisionGroups.BILL;
    this.physics.wakeUp(this.body);
    // Fresh flight — dice-vs-dice clatter stays muted until this die first
    // touches the floor or a wall (see touchedSurfaceThisFlight).
    this.touchedSurfaceThisFlight = false;
  }

  /**
   * Throw the dice
   * @param direction - Direction vector (will be scaled by power)
   * @param power - Throw power multiplier
   * @param spin - Optional angular velocity
   * @param mass - Optional mass (default 1.0) - heavier dice need more force, lighter fly farther
   */
  throw(
    direction: { x: number; y: number; z: number },
    power: number,
    spin?: { x: number; y: number; z: number },
    mass?: number
  ): void {
    this.release();

    // Set mass BEFORE applying impulse so it affects velocity correctly
    // Impulse = m * Δv, so Δv = impulse / m
    // Higher mass = lower velocity from same impulse = heavier dice don't fly as far
    if (mass !== undefined && mass > 0) {
      this.body.mass = mass;
      this.updateMassPropertiesSafe();
    }

    // Apply linear impulse
    this.physics.applyImpulse(this.body, {
      x: direction.x * power,
      y: direction.y * power,
      z: direction.z * power,
    });

    // Apply angular velocity for spin
    if (spin) {
      this.body.angularVelocity.set(spin.x, spin.y, spin.z);
    } else {
      // Random spin
      this.body.angularVelocity.set(
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20
      );
    }
  }

  /**
   * Reset dice to initial state
   */
  reset(position: { x: number; y: number; z: number }): void {
    this.physics.resetBody(this.body);
    this.setPosition(position);
    this.setRotation({ x: 0, y: 0, z: 0 });
    this.isHeld = false;
    this.body.mass = 1;
    this.updateMassPropertiesSafe();
  }

  /**
   * Check if dice is being held
   */
  get held(): boolean {
    return this.isHeld;
  }

  /**
   * Set ghosted (semi-transparent) visual state.
   * Traverses all descendant meshes for GLB model support.
   */
  setGhosted(ghosted: boolean): void {
    this.mesh.traverse((child) => {
      if (!(child as THREE.Mesh).isMesh) return;
      const m = child as THREE.Mesh;
      const materials = Array.isArray(m.material) ? m.material : [m.material];
      for (const material of materials) {
        material.transparent = ghosted;
        material.opacity = ghosted ? 0.35 : 1;
        material.depthWrite = !ghosted;
      }
    });
  }

  /**
   * Dispose of resources.
   * If this dice has a DiceAsset, the caller (DiceController) should call
   * DiceAssetManager.releaseDice() instead of relying on this method to
   * clean up the mesh — the pool manager handles that.
   */
  dispose(): void {
    // Remove debug box before release/dispose
    if (this.debugBox) {
      this.debugBox.parent?.remove(this.debugBox);
      this.debugBox.geometry.dispose();
      (this.debugBox.material as THREE.Material).dispose();
      this.debugBox = null;
    }

    // Remove from scene
    this.mesh.parent?.remove(this.mesh);
    // Drop the collision listener before the body is pooled/discarded, so a
    // reused asset.body doesn't accumulate stale listeners from old dice.
    this.body.removeEventListener('collide', this.onCollide as (e: unknown) => void);
    // Remove physics body from world. For asset dice the body stays on the
    // DiceAsset for pool reuse; for procedural dice it's fully discarded.
    this.physics.removeBody(this.body);

    // Only dispose geometry/materials for procedural dice (no asset).
    // Asset dice meshes + bodies are owned by the DiceAssetManager pool.
    if (!this.asset) {
      this.mesh.traverse((child) => {
        if (!(child as THREE.Mesh).isMesh) return;
        const m = child as THREE.Mesh;
        m.geometry?.dispose();
        if (Array.isArray(m.material)) {
          m.material.forEach((mat) => mat.dispose());
        } else {
          m.material?.dispose();
        }
      });
    }
  }
}
